// Merkan · Routen der App. Seiten werden bei Bedarf geladen.
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PASSWORT_SETZEN } from './lib/supabase';
import { useSession } from './lib/session';
import { useBranche } from './lib/branche';
import PasswortSetzen from './pages/PasswortSetzen';
import Anmelden from './pages/Anmelden';
import ZugangSchranke from './pages/Zugang';
import SupportLink from './hilfe/SupportLink';
import Rahmen, { MenueSeite } from './layout/Rahmen';
import { LadeZeilen, Leer } from './ui';
import { t } from './lib/sprache';

const Uebersicht = lazy(() => import('./seiten/uebersicht/Uebersicht'));
const Hub = lazy(() => import('./seiten/hub/Hub'));
const Postfach = lazy(() => import('./seiten/hub/Postfach'));
const Vorgaenge = lazy(() => import('./seiten/vorgaenge/Vorgaenge'));
const Vorgang = lazy(() => import('./seiten/vorgaenge/Vorgang'));
const Reklamationen = lazy(() => import('./seiten/vorgaenge/Reklamationen'));
const Bedarfe = lazy(() => import('./seiten/vorgaenge/Bedarfe'));
const Auswertungen = lazy(() => import('./seiten/auswertungen/Auswertungen'));
const Kunden = lazy(() => import('./seiten/stamm/Kunden'));
const Kunde = lazy(() => import('./seiten/stamm/Kunde'));
const Lieferanten = lazy(() => import('./seiten/stamm/Lieferanten'));
const Lieferant = lazy(() => import('./seiten/stamm/Lieferant'));
const Schiffe = lazy(() => import('./seiten/stamm/Schiffe'));
const Schiff = lazy(() => import('./seiten/stamm/Schiff'));
const Artikel = lazy(() => import('./seiten/stamm/Artikel'));
const Abgleich = lazy(() => import('./seiten/stamm/Abgleich'));
const ArtikelAkte = lazy(() => import('./seiten/stamm/ArtikelAkte'));
const Versanddienstleister = lazy(() => import('./seiten/stamm/Versanddienstleister'));
const Rechnungswesen = lazy(() => import('./seiten/rw/Rechnungswesen'));
const Kreditoren = lazy(() => import('./seiten/rw/Kreditoren'));
const Bank = lazy(() => import('./seiten/rw/Bank'));
const Beleg = lazy(() => import('./seiten/rw/Beleg'));
const Wiki = lazy(() => import('./seiten/wiki/Wiki'));
const Einstellungen = lazy(() => import('./seiten/einstellungen/Einstellungen'));
const SuchErgebnisse = lazy(() => import('./seiten/assistent/SuchErgebnisse'));

/** Seite nur mit Recht; sonst klarer Hinweis statt leerer Seite */
function Mit({ rechte, children }: { rechte: string[]; children: ReactNode }) {
  const { hat } = useSession();
  if (!hat(...rechte)) return <Leer titel={t('Kein Zugriff')} text={t('Deine Rolle hat keinen Zugriff auf diesen Bereich. Die Rechte vergibt parlinex im Admin Center.')} />;
  return <>{children}</>;
}

/** Wiki ohne Recht wiki: direkt zu den Anleitungen (die sieht jeder angemeldete Benutzer) */
function WissenOderAnleitungen() {
  const { rechte } = useSession();
  // Rechte kommen kurz nach dem Zugang; ohne jedes Recht nach kurzer Wartezeit weiter
  const [gewartet, setGewartet] = useState(false);
  useEffect(() => { const id = window.setTimeout(() => setGewartet(true), 1500); return () => window.clearTimeout(id); }, []);
  if (!rechte.length && !gewartet) return <LadeZeilen />;
  // Wiki zeigt ohne Recht wiki nur die Anleitungen
  return <Wiki />;
}

/** Schiffsakte nur für Mandanten mit Branche maritim; sonst zurück zur Übersicht */
function NurMitSchiffen({ children }: { children: ReactNode }) {
  const { schiffe } = useBranche();
  if (!schiffe) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { session, laden, mandantenGeladen, mandant, mandanten, zugang } = useSession();
  const [passwortOffen, setPasswortOffen] = useState(PASSWORT_SETZEN);

  if (laden) return <div className="laden">{t('Lädt …')}</div>;
  if (!session) return <Anmelden />;
  if (passwortOffen) return <PasswortSetzen titel={t('Willkommen bei Merkan')} fertig={() => setPasswortOffen(false)} />;
  if (!mandantenGeladen) return <div className="laden">{t('Zugriff wird geprüft …')}</div>;
  if (!mandant && mandanten.length === 0) {
    return <main className="zentriert"><div className="karte anmelde-karte"><h1>{t('Kein Mandant zugeordnet')}</h1><p className="leise">{t('Dein Konto ist noch keinem Unternehmen zugeordnet. Bitte wende dich an parlinex.')}</p></div></main>;
  }
  if (mandant && !zugang) return <div className="laden">{t('Zugriff wird geprüft …')}</div>;
  if (mandant && zugang && !zugang.ok) return <ZugangSchranke zugang={zugang} mandantName={mandant.name} mandantKurz={mandant.kurzname} />;

  const STAMM_LESEN = ['stamm', 'verkauf', 'hub', 'rw', 'einkauf'];
  return (
    <Rahmen>
      <Suspense fallback={<LadeZeilen />}>
        <Routes>
          <Route path="/" element={<Uebersicht />} />
          <Route path="/hub" element={<Mit rechte={['hub']}><Hub /></Mit>} />
          <Route path="/hub/:id" element={<Mit rechte={['hub', 'verkauf']}><Hub /></Mit>} />
          <Route path="/postfach" element={<Mit rechte={['hub']}><Postfach /></Mit>} />
          <Route path="/postfach/:id" element={<Mit rechte={['hub']}><Postfach /></Mit>} />
          <Route path="/vorgaenge" element={<Mit rechte={['verkauf']}><Vorgaenge /></Mit>} />
          <Route path="/vorgaenge/:id" element={<Mit rechte={['verkauf', 'hub', 'einkauf', 'versand', 'rw']}><Vorgang /></Mit>} />
          <Route path="/reklamationen" element={<Mit rechte={['verkauf', 'einkauf', 'versand', 'rw']}><Reklamationen /></Mit>} />
          <Route path="/bedarfe" element={<Mit rechte={['verkauf']}><Bedarfe /></Mit>} />
          <Route path="/auswertungen" element={<Mit rechte={['auswertung']}><Auswertungen /></Mit>} />
          <Route path="/kunden" element={<Mit rechte={STAMM_LESEN}><Kunden /></Mit>} />
          <Route path="/kunden/:id" element={<Mit rechte={STAMM_LESEN}><Kunde /></Mit>} />
          <Route path="/lieferanten" element={<Mit rechte={['stamm', 'einkauf']}><Lieferanten /></Mit>} />
          <Route path="/lieferanten/:id" element={<Mit rechte={['stamm', 'einkauf', 'hub', 'rw']}><Lieferant /></Mit>} />
          <Route path="/abgleich" element={<Mit rechte={STAMM_LESEN}><Abgleich /></Mit>} />
          <Route path="/schiffe" element={<NurMitSchiffen><Mit rechte={['stamm', 'verkauf', 'hub', 'versand', 'rw']}><Schiffe /></Mit></NurMitSchiffen>} />
          <Route path="/schiffe/:id" element={<NurMitSchiffen><Mit rechte={['stamm', 'verkauf', 'hub', 'versand', 'rw']}><Schiff /></Mit></NurMitSchiffen>} />
          <Route path="/artikel" element={<Mit rechte={['stamm', 'verkauf', 'einkauf', 'versand']}><Artikel /></Mit>} />
          <Route path="/artikel/:id" element={<Mit rechte={['stamm', 'verkauf', 'einkauf', 'versand']}><ArtikelAkte /></Mit>} />
          <Route path="/versanddienstleister" element={<Mit rechte={['versand', 'einkauf']}><Versanddienstleister /></Mit>} />
          <Route path="/versanddienstleister/:id" element={<Mit rechte={['versand', 'einkauf']}><Versanddienstleister /></Mit>} />
          <Route path="/rechnungswesen/*" element={<Mit rechte={['rw', 'verkauf']}><Rechnungswesen /></Mit>} />
          <Route path="/kreditoren/*" element={<Mit rechte={['rw']}><Kreditoren /></Mit>} />
          <Route path="/bank/*" element={<Mit rechte={['zahlung.buchen', 'datev.export', 'ustva.sehen', 'gobd.export']}><Bank /></Mit>} />
          <Route path="/rechnungswesen/beleg/:id" element={<Mit rechte={['rw', 'verkauf']}><Beleg /></Mit>} />
          <Route path="/wiki" element={<WissenOderAnleitungen />} />
          <Route path="/wiki/:id" element={<Mit rechte={['wiki']}><Wiki /></Mit>} />
          <Route path="/wiki/anleitungen" element={<WissenOderAnleitungen />} />
          <Route path="/wiki/anleitungen/:id" element={<Wiki />} />
          <Route path="/support" element={<SupportLink />} />
          <Route path="/einstellungen/*" element={<Einstellungen />} />
          <Route path="/menue" element={<MenueSeite />} />
          <Route path="/suche" element={<SuchErgebnisse />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Rahmen>
  );
}
